/**
 * Routing Rules Hooks
 * React Query hooks for routing rule management
 */

import { useQuery, useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { Sip } from '@sbclite/core';
import type { RoutingRuleCreateInputStrict, RoutingRuleUpdateInputStrict } from '@sbclite/core/schemas';
import { api } from '../services/api';

export const queryKeys = {
  routingRules: ['routing-rules'] as const,
} as const;

// Response types matching backend API
interface RoutingRulesResponse {
  success: boolean;
  data: Sip.RoutingRuleInfo[];
  metadata?: {
    limit: number;
    offset: number;
    count: number;
  };
}

interface RoutingRuleResponse {
  success: boolean;
  data: Sip.RoutingRuleInfo;
  message?: string;
}

interface DeleteResponse {
  success: boolean;
  message: string;
  data?: { ruleId: string };
}

// Create/Update input types
type CreateRoutingRuleInput = RoutingRuleCreateInputStrict;
type UpdateRoutingRuleInput = RoutingRuleUpdateInputStrict;

/**
 * Hook to fetch all routing rules
 */
export function useRoutingRules() {
  return useQuery<RoutingRulesResponse>({
    queryKey: queryKeys.routingRules,
    queryFn: async () => {
      const response = await api.get<RoutingRulesResponse>('/api/routing-rules');
      return response.data as RoutingRulesResponse;
    },
  });
}


/**
 * Hook to fetch a specific routing rule
 */
export function useRoutingRule(ruleId: string) {
  return useQuery<RoutingRuleResponse>({
    queryKey: ['routing-rules', ruleId],
    queryFn: async () => {
      const response = await api.get<RoutingRuleResponse>(`/api/routing-rules/${ruleId}`);
      return response.data as RoutingRuleResponse;
    },
    enabled: !!ruleId,
  });
}

/**
 * Hook to create a new routing rule
 */
export function useCreateRoutingRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateRoutingRuleInput) => {
      const response = await api.post<RoutingRuleResponse>('/api/routing-rules', input);
      return response.data as RoutingRuleResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.routingRules });
    },
  });
}

/**
 * Hook to update a routing rule
 */
export function useUpdateRoutingRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ruleId, updates }: { ruleId: string; updates: UpdateRoutingRuleInput }) => {
      const response = await api.put<RoutingRuleResponse>(`/api/routing-rules/${ruleId}`, updates);
      return response.data as RoutingRuleResponse;
    },
    onSuccess: (_data: RoutingRuleResponse, variables: { ruleId: string; updates: UpdateRoutingRuleInput }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.routingRules });
      queryClient.invalidateQueries({ queryKey: ['routing-rules', variables.ruleId] });
    },
  });
}

/**
 * Hook to delete a routing rule
 */
export function useDeleteRoutingRule(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ruleId: string) => {
      await api.delete<DeleteResponse>(`/api/routing-rules/${ruleId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.routingRules });
    },
  });
}

/**
 * Hook to toggle routing rule enabled status
 */
export function useToggleRoutingRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ruleId: string) => {
      const response = await api.post<RoutingRuleResponse>(`/api/routing-rules/${ruleId}/toggle`, {});
      return response.data as RoutingRuleResponse;
    },
    onSuccess: (_data: RoutingRuleResponse, ruleId: string) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.routingRules });
      queryClient.invalidateQueries({ queryKey: ['routing-rules', ruleId] });
    },
  });
}

// ========================================
// RFC 3261 COMPLIANCE HOOKS
// ========================================

/** RFC validation response */
interface RfcValidationResponse {
  success: boolean;
  data: {
    ruleId: string;
    ruleName: string;
    validation: {
      valid: boolean;
      issues: string[];
      warnings: string[];
      recommendations: string[];
      rfcCompliance: {
        recordRoute: boolean;
        maxForwards: boolean;
        viaHeader: boolean;
        routeHeaders: boolean;
      };
      overallCompliant: boolean;
      checkedAt: string;
    };
  };
  message: string;
}

/** RFC config update input */
interface RfcConfigInput {
  recordRouteEnabled?: boolean;
  decrementMaxForwards?: boolean;
  insertViaHeader?: boolean;
  routeHeaders?: string[];
  rfcConditions?: {
    requireRoute?: boolean;
    routePattern?: string;
    maxForwardsMin?: number;
    requireRecordRoute?: boolean;
  };
}

/** Rule test input */
interface TestRuleInput {
  testUri: string;
  direction?: 'inbound' | 'outbound';
}

/** Rule test response */
interface TestRuleResponse {
  success: boolean;
  data: {
    matches: boolean;
    ruleId: string;
    ruleName?: string;
    destinationTarget?: string;
    direction?: string;
    testTimestamp: string;
  };
  message: string;
}

/** Reorder response */
interface ReorderResponse {
  success: boolean;
  data: Sip.RoutingRuleInfo[];
  message: string;
  metadata: {
    reorderedCount: number;
    newPriorities: { ruleId: string; priority: number }[];
  };
}

/**
 * Hook to validate RFC 3261 compliance for a routing rule
 */
export function useValidateRfcCompliance() {
  return useMutation({
    mutationFn: async (ruleId: string) => {
      const response = await api.post<RfcValidationResponse>(
        `/api/routing-rules/${ruleId}/validate-rfc`,
        {}
      );
      return response.data as RfcValidationResponse;
    },
  });
}

/**
 * Hook to update RFC 3261 routing configuration
 */
export function useUpdateRfcConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ruleId, config }: { ruleId: string; config: RfcConfigInput }) => {
      const response = await api.patch<RoutingRuleResponse>(
        `/api/routing-rules/${ruleId}/rfc-config`,
        config
      );
      return response.data as RoutingRuleResponse;
    },
    onSuccess: (_data: RoutingRuleResponse, variables: { ruleId: string; config: RfcConfigInput }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.routingRules });
      queryClient.invalidateQueries({ queryKey: ['routing-rules', variables.ruleId] });
    },
  });
}

/**
 * Hook to test a routing rule against a SIP URI
 */
export function useTestRoutingRule() {
  return useMutation({
    mutationFn: async ({ ruleId, input }: { ruleId: string; input: TestRuleInput }) => {
      const response = await api.post<TestRuleResponse>(
        `/api/routing-rules/${ruleId}/test`,
        input
      );
      return response.data as TestRuleResponse;
    },
  });
}

/**
 * Hook to reorder routing rules by priority
 */
export function useReorderRoutingRules() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ruleIds: string[]) => {
      const response = await api.post<ReorderResponse>('/api/routing-rules/reorder', { ruleIds });
      return response.data as ReorderResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.routingRules });
    },
  });
}

// Export types for use in components
export type { 
  RoutingRulesResponse, 
  RoutingRuleResponse, 
  CreateRoutingRuleInput, 
  UpdateRoutingRuleInput,
  RfcValidationResponse,
  RfcConfigInput,
  TestRuleInput,
  TestRuleResponse
};
